/**
 * Single source of truth for country → phone dial code, and for resolving a
 * dial code from an IANA timezone.
 *
 * Before this module, two divergent hardcoded lists existed (Customers.js and
 * BusinessSetup.js) plus a timezone→country-name map (countryDetection.js).
 * Everything phone-country-related now composes from here so the pages cannot
 * drift apart.
 *
 * There is no phone-number/timezone npm library in the project, so a curated
 * mapping is the correct approach here (the "don't hand-roll a mapping" rule
 * only applies when a proper library is already available). Timezone→country is
 * reused from countryDetection.TIMEZONE_COUNTRY_MAP, which maps each IANA zone
 * to exactly one country — so the dial code for a given timezone is
 * deterministic even where a zone could theoretically span borders.
 */
import { detectCountryFromTimezone } from './countryDetection';

// country: display name, code: E.164 dial prefix, flag: emoji.
export const COUNTRY_DIAL_CODES = [
  { country: 'Afghanistan', code: '+93', flag: '🇦🇫' },
  { country: 'Albania', code: '+355', flag: '🇦🇱' },
  { country: 'Algeria', code: '+213', flag: '🇩🇿' },
  { country: 'American Samoa', code: '+1-684', flag: '🇦🇸' },
  { country: 'Andorra', code: '+376', flag: '🇦🇩' },
  { country: 'Angola', code: '+244', flag: '🇦🇴' },
  { country: 'Anguilla', code: '+1-264', flag: '🇦🇮' },
  { country: 'Antigua and Barbuda', code: '+1-268', flag: '🇦🇬' },
  { country: 'Argentina', code: '+54', flag: '🇦🇷' },
  { country: 'Armenia', code: '+374', flag: '🇦🇲' },
  { country: 'Aruba', code: '+297', flag: '🇦🇼' },
  { country: 'Australia', code: '+61', flag: '🇦🇺' },
  { country: 'Austria', code: '+43', flag: '🇦🇹' },
  { country: 'Azerbaijan', code: '+994', flag: '🇦🇿' },
  { country: 'Bahamas', code: '+1-242', flag: '🇧🇸' },
  { country: 'Bahrain', code: '+973', flag: '🇧🇭' },
  { country: 'Bangladesh', code: '+880', flag: '🇧🇩' },
  { country: 'Barbados', code: '+1-246', flag: '🇧🇧' },
  { country: 'Belarus', code: '+375', flag: '🇧🇾' },
  { country: 'Belgium', code: '+32', flag: '🇧🇪' },
  { country: 'Belize', code: '+501', flag: '🇧🇿' },
  { country: 'Benin', code: '+229', flag: '🇧🇯' },
  { country: 'Bermuda', code: '+1-441', flag: '🇧🇲' },
  { country: 'Bhutan', code: '+975', flag: '🇧🇹' },
  { country: 'Bolivia', code: '+591', flag: '🇧🇴' },
  { country: 'Bosnia and Herzegovina', code: '+387', flag: '🇧🇦' },
  { country: 'Botswana', code: '+267', flag: '🇧🇼' },
  { country: 'Brazil', code: '+55', flag: '🇧🇷' },
  { country: 'British Virgin Islands', code: '+1-284', flag: '🇻🇬' },
  { country: 'Brunei', code: '+673', flag: '🇧🇳' },
  { country: 'Bulgaria', code: '+359', flag: '🇧🇬' },
  { country: 'Burkina Faso', code: '+226', flag: '🇧🇫' },
  { country: 'Burundi', code: '+257', flag: '🇧🇮' },
  { country: 'Cabo Verde', code: '+238', flag: '🇨🇻' },
  { country: 'Cambodia', code: '+855', flag: '🇰🇭' },
  { country: 'Cameroon', code: '+237', flag: '🇨🇲' },
  { country: 'Canada', code: '+1', flag: '🇨🇦' },
  { country: 'Cayman Islands', code: '+1-345', flag: '🇰🇾' },
  { country: 'Central African Republic', code: '+236', flag: '🇨🇫' },
  { country: 'Chad', code: '+235', flag: '🇹🇩' },
  { country: 'Chile', code: '+56', flag: '🇨🇱' },
  { country: 'China', code: '+86', flag: '🇨🇳' },
  { country: 'Colombia', code: '+57', flag: '🇨🇴' },
  { country: 'Comoros', code: '+269', flag: '🇰🇲' },
  { country: 'Congo (DRC)', code: '+243', flag: '🇨🇩' },
  { country: 'Congo (Republic)', code: '+242', flag: '🇨🇬' },
  { country: 'Cook Islands', code: '+682', flag: '🇨🇰' },
  { country: 'Costa Rica', code: '+506', flag: '🇨🇷' },
  { country: "Cote d'Ivoire", code: '+225', flag: '🇨🇮' },
  { country: 'Croatia', code: '+385', flag: '🇭🇷' },
  { country: 'Cuba', code: '+53', flag: '🇨🇺' },
  { country: 'Curacao', code: '+599', flag: '🇨🇼' },
  { country: 'Cyprus', code: '+357', flag: '🇨🇾' },
  { country: 'Czech Republic', code: '+420', flag: '🇨🇿' },
  { country: 'Denmark', code: '+45', flag: '🇩🇰' },
  { country: 'Djibouti', code: '+253', flag: '🇩🇯' },
  { country: 'Dominica', code: '+1-767', flag: '🇩🇲' },
  { country: 'Dominican Republic', code: '+1-809', flag: '🇩🇴' },
  { country: 'Ecuador', code: '+593', flag: '🇪🇨' },
  { country: 'Egypt', code: '+20', flag: '🇪🇬' },
  { country: 'El Salvador', code: '+503', flag: '🇸🇻' },
  { country: 'Equatorial Guinea', code: '+240', flag: '🇬🇶' },
  { country: 'Eritrea', code: '+291', flag: '🇪🇷' },
  { country: 'Estonia', code: '+372', flag: '🇪🇪' },
  { country: 'Eswatini', code: '+268', flag: '🇸🇿' },
  { country: 'Ethiopia', code: '+251', flag: '🇪🇹' },
  { country: 'Falkland Islands', code: '+500', flag: '🇫🇰' },
  { country: 'Faroe Islands', code: '+298', flag: '🇫🇴' },
  { country: 'Fiji', code: '+679', flag: '🇫🇯' },
  { country: 'Finland', code: '+358', flag: '🇫🇮' },
  { country: 'France', code: '+33', flag: '🇫🇷' },
  { country: 'French Guiana', code: '+594', flag: '🇬🇫' },
  { country: 'French Polynesia', code: '+689', flag: '🇵🇫' },
  { country: 'Gabon', code: '+241', flag: '🇬🇦' },
  { country: 'Gambia', code: '+220', flag: '🇬🇲' },
  { country: 'Georgia', code: '+995', flag: '🇬🇪' },
  { country: 'Germany', code: '+49', flag: '🇩🇪' },
  { country: 'Ghana', code: '+233', flag: '🇬🇭' },
  { country: 'Gibraltar', code: '+350', flag: '🇬🇮' },
  { country: 'Greece', code: '+30', flag: '🇬🇷' },
  { country: 'Greenland', code: '+299', flag: '🇬🇱' },
  { country: 'Grenada', code: '+1-473', flag: '🇬🇩' },
  { country: 'Guadeloupe', code: '+590', flag: '🇬🇵' },
  { country: 'Guam', code: '+1-671', flag: '🇬🇺' },
  { country: 'Guatemala', code: '+502', flag: '🇬🇹' },
  { country: 'Guernsey', code: '+44-1481', flag: '🇬🇬' },
  { country: 'Guinea', code: '+224', flag: '🇬🇳' },
  { country: 'Guinea-Bissau', code: '+245', flag: '🇬🇼' },
  { country: 'Guyana', code: '+592', flag: '🇬🇾' },
  { country: 'Haiti', code: '+509', flag: '🇭🇹' },
  { country: 'Honduras', code: '+504', flag: '🇭🇳' },
  { country: 'Hong Kong', code: '+852', flag: '🇭🇰' },
  { country: 'Hungary', code: '+36', flag: '🇭🇺' },
  { country: 'Iceland', code: '+354', flag: '🇮🇸' },
  { country: 'India', code: '+91', flag: '🇮🇳' },
  { country: 'Indonesia', code: '+62', flag: '🇮🇩' },
  { country: 'Iran', code: '+98', flag: '🇮🇷' },
  { country: 'Iraq', code: '+964', flag: '🇮🇶' },
  { country: 'Ireland', code: '+353', flag: '🇮🇪' },
  { country: 'Isle of Man', code: '+44-1624', flag: '🇮🇲' },
  { country: 'Israel', code: '+972', flag: '🇮🇱' },
  { country: 'Italy', code: '+39', flag: '🇮🇹' },
  { country: 'Jamaica', code: '+1-876', flag: '🇯🇲' },
  { country: 'Japan', code: '+81', flag: '🇯🇵' },
  { country: 'Jersey', code: '+44-1534', flag: '🇯🇪' },
  { country: 'Jordan', code: '+962', flag: '🇯🇴' },
  { country: 'Kazakhstan', code: '+7', flag: '🇰🇿' },
  { country: 'Kenya', code: '+254', flag: '🇰🇪' },
  { country: 'Kiribati', code: '+686', flag: '🇰🇮' },
  { country: 'Kosovo', code: '+383', flag: '🇽🇰' },
  { country: 'Kuwait', code: '+965', flag: '🇰🇼' },
  { country: 'Kyrgyzstan', code: '+996', flag: '🇰🇬' },
  { country: 'Laos', code: '+856', flag: '🇱🇦' },
  { country: 'Latvia', code: '+371', flag: '🇱🇻' },
  { country: 'Lebanon', code: '+961', flag: '🇱🇧' },
  { country: 'Lesotho', code: '+266', flag: '🇱🇸' },
  { country: 'Liberia', code: '+231', flag: '🇱🇷' },
  { country: 'Libya', code: '+218', flag: '🇱🇾' },
  { country: 'Liechtenstein', code: '+423', flag: '🇱🇮' },
  { country: 'Lithuania', code: '+370', flag: '🇱🇹' },
  { country: 'Luxembourg', code: '+352', flag: '🇱🇺' },
  { country: 'Macau', code: '+853', flag: '🇲🇴' },
  { country: 'Madagascar', code: '+261', flag: '🇲🇬' },
  { country: 'Malawi', code: '+265', flag: '🇲🇼' },
  { country: 'Malaysia', code: '+60', flag: '🇲🇾' },
  { country: 'Maldives', code: '+960', flag: '🇲🇻' },
  { country: 'Mali', code: '+223', flag: '🇲🇱' },
  { country: 'Malta', code: '+356', flag: '🇲🇹' },
  { country: 'Marshall Islands', code: '+692', flag: '🇲🇭' },
  { country: 'Martinique', code: '+596', flag: '🇲🇶' },
  { country: 'Mauritania', code: '+222', flag: '🇲🇷' },
  { country: 'Mauritius', code: '+230', flag: '🇲🇺' },
  { country: 'Mayotte', code: '+262', flag: '🇾🇹' },
  { country: 'Mexico', code: '+52', flag: '🇲🇽' },
  { country: 'Micronesia', code: '+691', flag: '🇫🇲' },
  { country: 'Moldova', code: '+373', flag: '🇲🇩' },
  { country: 'Monaco', code: '+377', flag: '🇲🇨' },
  { country: 'Mongolia', code: '+976', flag: '🇲🇳' },
  { country: 'Montenegro', code: '+382', flag: '🇲🇪' },
  { country: 'Montserrat', code: '+1-664', flag: '🇲🇸' },
  { country: 'Morocco', code: '+212', flag: '🇲🇦' },
  { country: 'Mozambique', code: '+258', flag: '🇲🇿' },
  { country: 'Myanmar', code: '+95', flag: '🇲🇲' },
  { country: 'Namibia', code: '+264', flag: '🇳🇦' },
  { country: 'Nauru', code: '+674', flag: '🇳🇷' },
  { country: 'Nepal', code: '+977', flag: '🇳🇵' },
  { country: 'Netherlands', code: '+31', flag: '🇳🇱' },
  { country: 'New Caledonia', code: '+687', flag: '🇳🇨' },
  { country: 'New Zealand', code: '+64', flag: '🇳🇿' },
  { country: 'Nicaragua', code: '+505', flag: '🇳🇮' },
  { country: 'Niger', code: '+227', flag: '🇳🇪' },
  { country: 'Nigeria', code: '+234', flag: '🇳🇬' },
  { country: 'Niue', code: '+683', flag: '🇳🇺' },
  { country: 'Norfolk Island', code: '+672', flag: '🇳🇫' },
  { country: 'North Korea', code: '+850', flag: '🇰🇵' },
  { country: 'North Macedonia', code: '+389', flag: '🇲🇰' },
  { country: 'Northern Mariana Islands', code: '+1-670', flag: '🇲🇵' },
  { country: 'Norway', code: '+47', flag: '🇳🇴' },
  { country: 'Oman', code: '+968', flag: '🇴🇲' },
  { country: 'Pakistan', code: '+92', flag: '🇵🇰' },
  { country: 'Palau', code: '+680', flag: '🇵🇼' },
  { country: 'Palestine', code: '+970', flag: '🇵🇸' },
  { country: 'Panama', code: '+507', flag: '🇵🇦' },
  { country: 'Papua New Guinea', code: '+675', flag: '🇵🇬' },
  { country: 'Paraguay', code: '+595', flag: '🇵🇾' },
  { country: 'Peru', code: '+51', flag: '🇵🇪' },
  { country: 'Philippines', code: '+63', flag: '🇵🇭' },
  { country: 'Poland', code: '+48', flag: '🇵🇱' },
  { country: 'Portugal', code: '+351', flag: '🇵🇹' },
  { country: 'Puerto Rico', code: '+1-787', flag: '🇵🇷' },
  { country: 'Qatar', code: '+974', flag: '🇶🇦' },
  { country: 'Reunion', code: '+262', flag: '🇷🇪' },
  { country: 'Romania', code: '+40', flag: '🇷🇴' },
  { country: 'Russia', code: '+7', flag: '🇷🇺' },
  { country: 'Rwanda', code: '+250', flag: '🇷🇼' },
  { country: 'Saint Kitts and Nevis', code: '+1-869', flag: '🇰🇳' },
  { country: 'Saint Lucia', code: '+1-758', flag: '🇱🇨' },
  { country: 'Saint Vincent and the Grenadines', code: '+1-784', flag: '🇻🇨' },
  { country: 'Samoa', code: '+685', flag: '🇼🇸' },
  { country: 'San Marino', code: '+378', flag: '🇸🇲' },
  { country: 'Sao Tome and Principe', code: '+239', flag: '🇸🇹' },
  { country: 'Saudi Arabia', code: '+966', flag: '🇸🇦' },
  { country: 'Senegal', code: '+221', flag: '🇸🇳' },
  { country: 'Serbia', code: '+381', flag: '🇷🇸' },
  { country: 'Seychelles', code: '+248', flag: '🇸🇨' },
  { country: 'Sierra Leone', code: '+232', flag: '🇸🇱' },
  { country: 'Singapore', code: '+65', flag: '🇸🇬' },
  { country: 'Sint Maarten', code: '+1-721', flag: '🇸🇽' },
  { country: 'Slovakia', code: '+421', flag: '🇸🇰' },
  { country: 'Slovenia', code: '+386', flag: '🇸🇮' },
  { country: 'Solomon Islands', code: '+677', flag: '🇸🇧' },
  { country: 'Somalia', code: '+252', flag: '🇸🇴' },
  { country: 'South Africa', code: '+27', flag: '🇿🇦' },
  { country: 'South Korea', code: '+82', flag: '🇰🇷' },
  { country: 'South Sudan', code: '+211', flag: '🇸🇸' },
  { country: 'Spain', code: '+34', flag: '🇪🇸' },
  { country: 'Sri Lanka', code: '+94', flag: '🇱🇰' },
  { country: 'Sudan', code: '+249', flag: '🇸🇩' },
  { country: 'Suriname', code: '+597', flag: '🇸🇷' },
  { country: 'Sweden', code: '+46', flag: '🇸🇪' },
  { country: 'Switzerland', code: '+41', flag: '🇨🇭' },
  { country: 'Syria', code: '+963', flag: '🇸🇾' },
  { country: 'Taiwan', code: '+886', flag: '🇹🇼' },
  { country: 'Tajikistan', code: '+992', flag: '🇹🇯' },
  { country: 'Tanzania', code: '+255', flag: '🇹🇿' },
  { country: 'Thailand', code: '+66', flag: '🇹🇭' },
  { country: 'Timor-Leste', code: '+670', flag: '🇹🇱' },
  { country: 'Togo', code: '+228', flag: '🇹🇬' },
  { country: 'Tokelau', code: '+690', flag: '🇹🇰' },
  { country: 'Tonga', code: '+676', flag: '🇹🇴' },
  { country: 'Trinidad and Tobago', code: '+1-868', flag: '🇹🇹' },
  { country: 'Tunisia', code: '+216', flag: '🇹🇳' },
  { country: 'Turkey', code: '+90', flag: '🇹🇷' },
  { country: 'Turkmenistan', code: '+993', flag: '🇹🇲' },
  { country: 'Turks and Caicos Islands', code: '+1-649', flag: '🇹🇨' },
  { country: 'Tuvalu', code: '+688', flag: '🇹🇻' },
  { country: 'Uganda', code: '+256', flag: '🇺🇬' },
  { country: 'Ukraine', code: '+380', flag: '🇺🇦' },
  { country: 'United Arab Emirates', code: '+971', flag: '🇦🇪' },
  { country: 'United Kingdom', code: '+44', flag: '🇬🇧' },
  { country: 'United States', code: '+1', flag: '🇺🇸' },
  { country: 'Uruguay', code: '+598', flag: '🇺🇾' },
  { country: 'US Virgin Islands', code: '+1-340', flag: '🇻🇮' },
  { country: 'Uzbekistan', code: '+998', flag: '🇺🇿' },
  { country: 'Vanuatu', code: '+678', flag: '🇻🇺' },
  { country: 'Vatican City', code: '+379', flag: '🇻🇦' },
  { country: 'Venezuela', code: '+58', flag: '🇻🇪' },
  { country: 'Vietnam', code: '+84', flag: '🇻🇳' },
  { country: 'Wallis and Futuna', code: '+681', flag: '🇼🇫' },
  { country: 'Yemen', code: '+967', flag: '🇾🇪' },
  { country: 'Zambia', code: '+260', flag: '🇿🇲' },
  { country: 'Zimbabwe', code: '+263', flag: '🇿🇼' },
  { country: 'Other', code: '+1', flag: '🌐' },
];

const DEFAULT_COUNTRY = 'United States';
const DEFAULT_CODE = '+1';

const COUNTRY_TO_ENTRY = COUNTRY_DIAL_CODES.reduce((acc, entry) => {
  acc[entry.country] = entry;
  return acc;
}, {});

/**
 * Dial code for a country display name. Falls back to +1 for unknown names so a
 * missing entry never produces an empty prefix.
 * @param {string} country
 * @returns {string}
 */
export function getDialCode(country) {
  const match = COUNTRY_TO_ENTRY[country];
  return match ? match.code : DEFAULT_CODE;
}

/**
 * Splits a stored E.164-ish phone string into its dial code, owning country and
 * local number. Longest-prefix match so +1-684 wins over +1.
 * @param {string} phoneStr
 * @returns {{code: string, country: string, number: string}}
 */
export function parsePhone(phoneStr) {
  if (!phoneStr) return { code: DEFAULT_CODE, country: DEFAULT_COUNTRY, number: '' };
  const sorted = [...COUNTRY_DIAL_CODES].sort((a, b) => b.code.length - a.code.length);
  for (const c of sorted) {
    if (phoneStr.startsWith(c.code)) {
      return { code: c.code, country: c.country, number: phoneStr.slice(c.code.length) };
    }
  }
  return { code: DEFAULT_CODE, country: DEFAULT_COUNTRY, number: phoneStr.replace(/^\+/, '') };
}

/**
 * Resolves an IANA timezone to a { country, code } pair.
 *
 * Composes the existing timezone→country-name map with the dial-code table, so
 * there is exactly one place that knows which country a timezone belongs to.
 * The map is 1:1 (each zone → one country), which makes the result
 * deterministic for zones that could otherwise be ambiguous.
 *
 * @param {string} timezone e.g. 'Asia/Kolkata'
 * @returns {{country: string, code: string}}
 */
export function getCountryCodeFromTimezone(timezone) {
  const country = detectCountryFromTimezone(timezone) || DEFAULT_COUNTRY;
  const entry = COUNTRY_TO_ENTRY[country];
  if (entry) return { country: entry.country, code: entry.code };
  return { country: DEFAULT_COUNTRY, code: DEFAULT_CODE };
}

/**
 * Dial code for the browser's current timezone. Safe to call at render time;
 * falls back to +1 if the Intl API is unavailable.
 * @returns {string}
 */
export function getDialCodeFromBrowserTimezone() {
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    return getCountryCodeFromTimezone(tz).code;
  } catch {
    return DEFAULT_CODE;
  }
}
